import { useCallback } from 'react';

import { useLoadImage } from 'src/app/shared/hooks/image';

import type { VictoriousPetition } from '../../../../../../../../shared/api';
import { useSuggestedPetitionsTracking } from '../../../../../../../../shared/hooks/useSuggestedPetitionsTracking';
import type {
	SuggestedPetitionClickLocation,
	SuggestedPetitionSourceLocation,
} from '../../../../../../../../shared/types';
import { generatePetitionUrl } from '../../../../../../../../shared/utils';

type Options = {
	parentPetitionId?: string;
	petition: VictoriousPetition;
	gridPosition: number;
	sourceLocation: SuggestedPetitionSourceLocation;
};

type Result = ModelHookResult<
	{
		petitionUrl: string;
		photoUrl?: string;
	},
	{
		trackClick: (location: SuggestedPetitionClickLocation) => void;
	}
>;

const MAX_ATTEMPTS = 10;
const POLLING_TIME = 3 * 1000; // 3sec

export function useVictoriousPetitionCard({
	petition,
	parentPetitionId,
	sourceLocation,
	gridPosition,
}: Options): Result {
	const {
		actions: { trackClick },
	} = useSuggestedPetitionsTracking({
		parentPetitionId,
		petitionStatus: 'victorious',
	});

	const {
		node: { photo },
	} = petition;

	const finalPhoto = photo?.petitionSmall;
	const imageLoaded = useLoadImage(finalPhoto?.url, {
		maxAttempts: MAX_ATTEMPTS,
		pollingTime: POLLING_TIME,
		processing: finalPhoto?.processing,
	});

	return {
		data: {
			photoUrl: imageLoaded || finalPhoto ? finalPhoto?.url : '',
			...generatePetitionUrl({ petition, gridPosition, parentPetitionId, sourceLocation }),
		},
		actions: {
			trackClick: useCallback(
				(location) => trackClick(location, petition, gridPosition),
				[trackClick, petition, gridPosition],
			),
		},
	};
}
